import { useCallback, useEffect, useMemo, useState } from "react";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import TableCardv2 from "../../components/TableCardv2";
import { debounce } from "lodash";
import {
  atualizarContaResultado,
  buscarContasResultado,
  buscarContasResultadoPorTexto,
  excluirContaResultado,
  inserirContaResultado,
} from "../../services/api-planejamento-tributario.service";
import SnackBar from "../../components/SnackBar";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Button,
} from "@mui/material";
import BlueButton from "../../components/BlueButton";
import ModalConfirmacao from "../../components/ModalConfirmacao";
import styles from "./styles.module.css";

const TIPO_PADRAO = "Conta de Resultado";

const header = {
  icon: <AccountBalanceIcon fontSize="1rem" />,
  title: "Contas de Resultado",
};

const ContasResultado = () => {
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    codigo: "",
    descricao: "",
    ativo: true,
  });
  const [isUpdate, setIsUpdate] = useState(false);

  const columns = useMemo(
    () => [
      { key: "codigo", label: "Código" },
      { key: "descricao", label: "Descrição" },
      { key: "statusAtivo", label: "Ativo", align: "center", width: "100px" },
    ],
    []
  );

  const handleSnackBar = useCallback((obj) => {
    setObjSnackBar(obj);
  }, []);

  const formatAtivo = useCallback((ativo) => {
    const sim = Boolean(ativo);
    return (
      <span className={styles.status_container}>
        <span className={sim ? styles.ativo_sim : styles.ativo_nao} />
        {sim ? "Sim" : "Não"}
      </span>
    );
  }, []);

  const handleOrganizaDados = useCallback(
    (lista) =>
      (lista || []).map((item) => ({
        id: item.id,
        codigo: item.codigo,
        descricao: item.descricao,
        statusAtivo: formatAtivo(item.ativo),
        btn: [
          {
            icon: <EditIcon />,
            title: "Editar",
            onClick: () => {
              setIsUpdate(true);
              setFormData({
                id: item.id,
                codigo: item.codigo ?? "",
                descricao: item.descricao ?? "",
                ativo: Boolean(item.ativo),
              });
              setOpenEditModal(true);
            },
            variant: "primary",
          },
          {
            icon: <DeleteIcon />,
            title: "Excluir",
            onClick: () => {
              setFormData({
                id: item.id,
                codigo: item.codigo ?? "",
                descricao: item.descricao ?? "",
                ativo: Boolean(item.ativo),
              });
              setOpenDeleteModal(true);
            },
            variant: "danger",
          },
        ],
      })),
    [formatAtivo]
  );

  const handleInititalData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await buscarContasResultado();
      const lista = response?.contas ?? [];
      setContas(handleOrganizaDados(lista));
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          "Erro ao carregar contas de resultado.",
      });
    } finally {
      setLoading(false);
    }
  }, [handleOrganizaDados, handleSnackBar]);

  useEffect(() => {
    handleInititalData();
  }, [handleInititalData]);

  const handlePesquisaPorTexto = useCallback(
    async (texto) => {
      setLoadingTable(true);
      try {
        const response =
          texto === "" || texto == null
            ? await buscarContasResultado()
            : await buscarContasResultadoPorTexto(texto);
        const lista = response?.contas ?? [];
        setContas(handleOrganizaDados(lista));
      } catch (error) {
        handleSnackBar({
          type: "error",
          msg:
            error.response?.data?.mensagem ||
            error.response?.data?.message ||
            "Erro na pesquisa.",
        });
      } finally {
        setLoadingTable(false);
      }
    },
    [handleOrganizaDados, handleSnackBar]
  );

  const handleDebounce = useMemo(
    () => debounce((value) => handlePesquisaPorTexto(value), 300),
    [handlePesquisaPorTexto]
  );

  useEffect(() => {
    return () => handleDebounce.cancel();
  }, [handleDebounce]);

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setIsUpdate(false);
    setFormData({
      id: null,
      codigo: "",
      descricao: "",
      ativo: true,
    });
  };

  const handleAbrirNovo = () => {
    setIsUpdate(false);
    setFormData({
      id: null,
      codigo: "",
      descricao: "",
      ativo: true,
    });
    setOpenEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.codigo?.trim() || !formData.descricao?.trim()) {
      handleSnackBar({
        type: "error",
        msg: "Código e descrição são obrigatórios.",
      });
      return;
    }
    try {
      const payload = {
        codigo: formData.codigo.trim(),
        descricao: formData.descricao.trim(),
        ativo: formData.ativo,
        tipo: TIPO_PADRAO,
      };
      const response = isUpdate
        ? await atualizarContaResultado(formData.id, payload)
        : await inserirContaResultado(payload);
      handleSnackBar({
        type: "success",
        msg: response.mensagem || response.message || "Salvo com sucesso.",
      });
      await handleInititalData();
      handleCloseEditModal();
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          "Erro ao salvar.",
      });
    }
  };

  const handleConfirmarExclusao = async () => {
    try {
      const response = await excluirContaResultado(formData.id);
      handleSnackBar({
        type: "success",
        msg: response.mensagem || response.message || "Excluído com sucesso.",
      });
      await handleInititalData();
    } catch (error) {
      handleSnackBar({
        type: "error",
        msg:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          "Erro ao excluir.",
      });
    }
  };

  return (
    <>
      <div className={styles.container}>
        <TableCardv2
          header={header}
          columns={columns}
          data={contas}
          debounce={handleDebounce}
          loading={loading}
          loadingTable={loadingTable}
          searchPlaceholder="Buscar por código ou descrição"
        />
      </div>

      <Fab
        sx={{
          background: "#4b6fc0",
          bottom: 40,
          right: 20,
          position: "fixed",
          zIndex: 1000,
          "&:hover": {
            backgroundColor: "#4b6fc0",
          },
        }}
        aria-label="Nova conta de resultado"
        onClick={handleAbrirNovo}
      >
        <AddIcon sx={{ color: "#fff" }} />
      </Fab>

      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSave}>
          <DialogTitle
            sx={{
              bgcolor: "#18316e",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <span>
              {isUpdate ? "Editar conta de resultado" : "Nova conta de resultado"}
            </span>
            <IconButton
              aria-label="fechar"
              onClick={handleCloseEditModal}
              sx={{ color: "white", padding: 0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 0.5 }}>
            <TextField
              label="Código"
              value={formData.codigo}
              required
              onChange={(e) =>
                setFormData({ ...formData, codigo: e.target.value })
              }
              fullWidth
              size="small"
              margin="dense"
            />
            <TextField
              label="Descrição"
              value={formData.descricao}
              required
              onChange={(e) =>
                setFormData({ ...formData, descricao: e.target.value })
              }
              fullWidth
              size="small"
              margin="dense"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={(e) =>
                    setFormData({ ...formData, ativo: e.target.checked })
                  }
                />
              }
              label="Ativo"
              sx={{ mt: 0.5, mb: 0, display: "block" }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1, pt: 0.5, gap: 1 }}>
            <Button onClick={handleCloseEditModal} color="inherit" variant="outlined">
              Cancelar
            </Button>
            <BlueButton textValue="Salvar" type="submit" />
          </DialogActions>
        </form>
      </Dialog>

      <ModalConfirmacao
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmarExclusao}
        title="Excluir conta"
        message={`Tem certeza que deseja excluir a conta "${formData.codigo} — ${formData.descricao}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="error"
      />

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={handleSnackBar} />
    </>
  );
};

export default ContasResultado;
